import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, unref as _unref, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "mb-4 flex flex-wrap items-end gap-3" }
const _hoisted_2 = { class: "mb-3 flex flex-wrap items-center justify-between gap-3" }
const _hoisted_3 = { class: "flex items-center gap-2 text-sm text-gray-700" }
const _hoisted_4 = ["checked", "disabled"]
const _hoisted_5 = { class: "flex items-center gap-3" }
const _hoisted_6 = { class: "text-sm text-gray-600" }
const _hoisted_7 = {
  key: 0,
  class: "mb-3 text-sm text-amber-700"
}
const _hoisted_8 = {
  key: 1,
  class: "mb-3 text-sm text-red-600",
  role: "alert"
}
const _hoisted_9 = {
  key: 0,
  class: "divide-y divide-gray-200 overflow-hidden rounded-xl border border-gray-200 bg-white"
}
const _hoisted_10 = ["aria-label", "checked", "onChange"]
const _hoisted_11 = { class: "min-w-0 flex-1" }
const _hoisted_12 = { class: "truncate text-sm font-medium text-gray-900" }
const _hoisted_13 = { class: "truncate text-xs text-gray-500" }
const _hoisted_14 = { class: "shrink-0 text-sm text-gray-700" }

import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import EmptyState from '@/components/neue/EmptyState.vue'
import InputField from '@/components/neue/InputField.vue'
import NeueButton from '@/components/neue/NeueButton.vue'
import NeueLoading from '@/components/neue/NeueLoading.vue'
import NeueNumberedPagination from '@/components/neue/NeueNumberedPagination.vue'
import SelectInput from '@/components/neue/SelectInput.vue'
import { isPastLastPage, MAX_ITEMS_PER_CREATE_RUN, priceText } from '@/helpers/parsley'
import { trackParsleyRun, useParsleyRunTracker } from '@/helpers/parsleyRunTracker'
import { errorMessage, parsleyApi } from '@/services/parsleyApi'

const PAGE_SIZE = 25
const SEARCH_DELAY_MS = 300
const INTRO =
  'Parsley recipes that have no product in Bottle yet. Creating one adds it to your product library with its name, description, price, photo and nutrition. Add it to a store when you are ready to sell it.'
const EMPTY_NOTE = 'Every Parsley recipe that matches already has a product.'
const LOAD_ERROR = "Couldn't load your Parsley items. Please refresh the page."
const CREATE_ERROR = "Couldn't start creating products. Please try again."


export default {
  __name: 'ParsleyNewItemsTab',
  setup(__props) {

const ALL_TAGS = { name: 'All tags', value: '' }
const TOO_MANY_NOTE = `Select ${MAX_ITEMS_PER_CREATE_RUN} items or fewer at a time.`
const tag = ref('')
const search = ref('')
const page = ref(1)
const items = ref([])
const availableTags = ref([])
const pageCount = ref(0)
const totalCount = ref(0)
const loading = ref(true)
const starting = ref(false)
const error = ref('')
// Ids, not rows, so a selection survives paging.
const selected = ref([])

const { running, finished } = useParsleyRunTracker()

let searchTimer = null
let searchedFor = ''
let latest = 0

const tagOptions = computed(() => [ALL_TAGS, ...availableTags.value.map(name => ({ name, value: name }))])
const tooMany = computed(() => selected.value.length > MAX_ITEMS_PER_CREATE_RUN)
const canCreate = computed(() => selected.value.length > 0 && !tooMany.value && !running.value && !starting.value)
const pageSelected = computed(
  () => items.value.length > 0 && items.value.every(item => selected.value.includes(item.id))
)

const params = () => ({
  type: 'recipe',
  linked: 'false',
  active: 'true',
  tag: tag.value,
  search: String(search.value ?? '').trim(),
  page: page.value,
  page_size: PAGE_SIZE
})

const load = async () => {
  const mine = (latest += 1)
  loading.value = true
  error.value = ''
  try {
    const result = await parsleyApi.menuItems(params())
    if (mine !== latest) return
    if (isPastLastPage(result, page.value)) {
      goTo(Math.max(result.total_pages, 1))
      return
    }
    items.value = result.data
    availableTags.value = result.available_tags ?? []
    pageCount.value = result.total_pages
    totalCount.value = result.total_records
  } catch (failure) {
    if (mine !== latest) return
    error.value = errorMessage(failure, LOAD_ERROR)
  } finally {
    if (mine === latest) loading.value = false
  }
}

const loadFirstPage = () => {
  page.value = 1
  load()
}

const goTo = next => {
  page.value = next
  load()
}

const clearSelection = () => {
  selected.value = []
}

const toggle = (id, checked) => {
  const others = selected.value.filter(existing => existing !== id)
  selected.value = checked ? [...others, id] : others
}

const togglePage = checked => {
  const onPage = items.value.map(item => item.id)
  const others = selected.value.filter(id => !onPage.includes(id))
  selected.value = checked ? [...others, ...onPage] : others
}

const create = async () => {
  if (!canCreate.value) return
  starting.value = true
  error.value = ''
  try {
    trackParsleyRun({ run: await parsleyApi.startCreate(selected.value), fetchRun: parsleyApi.run })
    selected.value = []
  } catch (failure) {
    error.value = errorMessage(failure, CREATE_ERROR)
  } finally {
    starting.value = false
  }
}

// A selection survives paging but not a change of filter, so Create never
// includes items the merchant can no longer see.
watch(tag, () => {
  clearSelection()
  loadFirstPage()
})

// The selection goes when the search runs, and only if what is searched for
// changed, so typing and deleting a character keeps it.
const runSearch = () => {
  const text = String(search.value ?? '').trim()
  if (text !== searchedFor) clearSelection()
  searchedFor = text
  loadFirstPage()
}

watch(search, () => {
  if (searchTimer) clearTimeout(searchTimer)
  searchTimer = setTimeout(runSearch, SEARCH_DELAY_MS)
})

// Items that became products no longer belong in this list.
watch(finished, isFinished => {
  if (isFinished) load()
})

onMounted(load)

onBeforeUnmount(() => {
  if (searchTimer) clearTimeout(searchTimer)
  latest += 1
})

return (_ctx, _cache) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("p", { class: "mb-4 max-w-2xl text-sm text-gray-600" }, _toDisplayString(INTRO)),
    _createElementVNode("div", _hoisted_1, [
      _createVNode(SelectInput, {
        modelValue: tag.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = $event => ((tag).value = $event)),
        name: "Parsley tag",
        options: tagOptions.value,
        class: "w-64"
      }, null, 8, ["modelValue", "options"]),
      _createVNode(InputField, {
        modelValue: search.value,
        "onUpdate:modelValue": _cache[1] || (_cache[1] = $event => ((search).value = $event)),
        placeholder: "Search Parsley recipes",
        class: "w-64"
      }, null, 8, ["modelValue"])
    ]),
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("label", _hoisted_3, [
        _createElementVNode("input", {
          "data-test": "select-page",
          type: "checkbox",
          class: "h-4 w-4 rounded border-gray-300",
          checked: pageSelected.value,
          disabled: !items.value.length,
          onChange: _cache[2] || (_cache[2] = $event => (togglePage($event.target.checked)))
        }, null, 40, _hoisted_4),
        _cache[3] || (_cache[3] = _createTextVNode(" Select everything on this page ", -1))
      ]),
      _createElementVNode("div", _hoisted_5, [
        _createElementVNode("span", _hoisted_6, _toDisplayString(selected.value.length) + " selected", 1),
        (selected.value.length)
          ? (_openBlock(), _createElementBlock("button", {
              key: 0,
              type: "button",
              class: "text-sm font-medium text-brand-600 hover:text-brand-700",
              onClick: clearSelection
            }, " Clear selection "))
          : _createCommentVNode("", true),
        _createVNode(NeueButton, {
          "data-test": "create",
          type: canCreate.value ? 'primary' : 'inactive',
          loading: starting.value,
          onClick: create
        }, {
          default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
            _createTextVNode(" Create in Bottle ", -1)
          ]))]),
          _: 1
        }, 8, ["type", "loading"])
      ])
    ]),
    (tooMany.value)
      ? (_openBlock(), _createElementBlock("p", _hoisted_7, _toDisplayString(TOO_MANY_NOTE)))
      : _createCommentVNode("", true),
    (error.value)
      ? (_openBlock(), _createElementBlock("p", _hoisted_8, _toDisplayString(error.value), 1))
      : _createCommentVNode("", true),
    _createVNode(NeueLoading, {
      loading: loading.value && !items.value.length
    }, {
      default: _withCtx(() => [
        (items.value.length)
          ? (_openBlock(), _createElementBlock("ul", _hoisted_9, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(items.value, (item) => {
                return (_openBlock(), _createElementBlock("li", {
                  key: item.id,
                  "data-test": "item",
                  class: "flex items-center gap-4 px-5 py-3"
                }, [
                  _createElementVNode("input", {
                    type: "checkbox",
                    class: "h-4 w-4 rounded border-gray-300",
                    "aria-label": `Select ${item.attributes.name}`,
                    checked: selected.value.includes(item.id),
                    onChange: $event => (toggle(item.id, $event.target.checked))
                  }, null, 40, _hoisted_10),
                  _createElementVNode("div", _hoisted_11, [
                    _createElementVNode("div", _hoisted_12, _toDisplayString(item.attributes.name), 1),
                    _createElementVNode("div", _hoisted_13, _toDisplayString(item.attributes.tags.join(', ')), 1)
                  ]),
                  _createElementVNode("div", _hoisted_14, _toDisplayString(_unref(priceText)(item.attributes.price_cents)), 1)
                ]))
              }), 128))
            ]))
          : (!loading.value && !error.value)
            ? (_openBlock(), _createBlock(EmptyState, {
                key: 1,
                plural: "Parsley recipes",
                description: EMPTY_NOTE
              }))
            : _createCommentVNode("", true)
      ]),
      _: 1
    }, 8, ["loading"]),
    _createVNode(NeueNumberedPagination, {
      page: page.value,
      "page-count": pageCount.value,
      "total-count": totalCount.value,
      "page-size": PAGE_SIZE,
      loading: loading.value,
      onChange: goTo
    }, null, 8, ["page", "page-count", "total-count", "loading"])
  ]))
}
}

}